import SupplierNotesPanel from './SupplierNotesPanel';
import { formSchema } from '../config/supplierForm.schema';
import { useEffect, useRef, useState } from 'react';
import { Building2 } from '../../../shared/assets/AppIcons';
import { emptySupplier } from '../supplier-defaults';
import { toast } from '../../../shared/hooks/use-toast';
import { PartyField, PartySelect, PartyFormSection } from '../../../shared/components/PartyFormFields';
import EntityMultiSelect from '../../../shared/components/EntityMultiSelect';
import { listStaffResult } from '../../staff/services/staff.service';
import ClientCustomFieldsSection from '../../clients/components/ClientCustomFieldsSection';
import {
  archiveSupplierCustomField,
  createSupplierCustomField,
  listSupplierCustomFields,
} from '../services/suppliers.service';
import {
  CreateFormCancelButton,
  CreateFormDialog,
  CreateFormSubmitButton,
} from '../../../shared/components/CreateFormDialog';

const SUPPLIER_CUSTOM_FIELDS_API = {
  list: listSupplierCustomFields,
  create: createSupplierCustomField,
  archive: archiveSupplierCustomField,
};

export default function SupplierDialog({ open, supplier, onClose, onSave }) {
  const [form, setForm] = useState(() => supplier ? { ...emptySupplier, ...supplier, displayName: supplier.displayName || supplier.legalName || '' } : emptySupplier);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const editing = Boolean(supplier?.id);
  const [staff, setStaff] = useState([]);
  const [staffMessage, setStaffMessage] = useState('');
  const legalForms = formSchema.field('legalForm').options;

  useEffect(() => {
    setForm(supplier ? { ...emptySupplier, ...supplier, displayName: supplier.displayName || supplier.legalName || '' } : emptySupplier);
    setIsSubmitting(false);
    submittingRef.current = false;
  }, [supplier, open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setStaff([]);
    setStaffMessage('Caricamento personale...');
    listStaffResult().then(result => {
      if (cancelled) return;
      setStaff(result.ok ? result.data : []);
      setStaffMessage(result.ok ? '' : result.message || 'Catalogo personale non disponibile.');
    }).catch(() => { if (!cancelled) setStaffMessage('Catalogo personale non disponibile.'); });
    return () => { cancelled = true; };
  }, [open]);

  const updateField = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const field = (name, id, props = {}) => <PartyField id={`supplier-${id}`} label={formSchema.field(name).label}
    value={form[name]} onChange={value => updateField(name, value)} disabled={isSubmitting} {...props} />;

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await onSave(editing ? form : { ...form, status: 'ACTIVE' });
      if (result?.ok) onClose();
      else
        toast({
          title: 'Fornitore non salvato',
          description: result?.message || 'Impossibile salvare il fornitore.',
          variant: 'destructive',
        });
    } catch (error) {
      toast({
        title: 'Fornitore non salvato',
        description: error.message || 'Impossibile salvare il fornitore.',
        variant: 'destructive',
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <CreateFormDialog
      open={open}
      busy={isSubmitting}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      icon={Building2}
      title={editing ? 'Modifica fornitore' : 'Nuovo Fornitore'}
      className="party-form-dialog"
      footer={
        <>
          <CreateFormCancelButton onClick={onClose} disabled={isSubmitting} />
          <CreateFormSubmitButton
            saving={isSubmitting}
            className="supplier-dialog__submit"
            form="supplier-form"
          >
            Salva fornitore
          </CreateFormSubmitButton>
        </>
      }
    >
      <form id="supplier-form" className="party-form create-form-dialog__form" onSubmit={submit}>
        <div className="party-form__row">
          {field('displayName', 'display-name', { label: 'Azienda', required: true, placeholder: 'Esempio: ACME Distribution S.r.l.' })}
          {field('ownerName', 'owner-name', { placeholder: 'Esempio: Giovanni Rossi' })}
        </div>
        {field('notes', 'description', { label: 'Descrizione', multiline: true, rows: 3, maxLength: 4000, placeholder: 'Inserisci una descrizione sul fornitore...' })}
        <div className="party-form__row">
          {field('contactName', 'contact-name', { placeholder: 'Nome del referente di contatto' })}
          {field('contactRole', 'contact-role', { placeholder: 'Esempio: Account Manager, Logistica...' })}
        </div>
        <PartySelect id="supplier-category" label="Categoria" value={form.category}
          onChange={value => updateField('category', value)} options={formSchema.field('category').options}
          placeholder="Seleziona categoria merceologica..." disabled={isSubmitting} />
        <div className="party-form__row">
          {field('taxCode', 'tax-code', { placeholder: '16 caratteri alfanumerici' })}
          {field('vatNumber', 'vat-number', { label: 'P.IVA', required: true, placeholder: '11 cifre numeriche' })}
        </div>
        <div className="party-form__row">
          {field('reaNumber', 'rea', { placeholder: 'Esempio: MI-123456' })}
          <PartySelect id="supplier-legal-form" label="Forma giuridica" value={form.legalForm}
            onChange={value => updateField('legalForm', value)} options={legalForms}
            placeholder="Seleziona forma..." disabled={isSubmitting} />
        </div>
        <div className="party-form__row">
          {field('sector', 'sector', { placeholder: 'Esempio: Servizi, Forniture elettriche...' })}
          {field('legalName', 'legal-name', { required: true, placeholder: 'Esempio: Rossi S.r.l.' })}
        </div>
        {field('registeredAddress', 'address', { placeholder: 'Via, Numero, CAP, Città, Provincia' })}
        <PartyFormSection title="Contatti">
          <div className="party-form__row">
            {field('contactEmail', 'contact-email', { label: 'Email personale', type: 'email', placeholder: 'mario.rossi@email.it' })}
            {field('contactPhone', 'contact-phone', { label: 'Telefono personale', type: 'tel', placeholder: '+39 347 1234567' })}
          </div>
          <div className="party-form__row">
            {field('companyEmail', 'company-email', { type: 'email', placeholder: 'commerciale@fornitore.it' })}
            {field('companyPhone', 'company-phone', { type: 'tel', placeholder: '+39 02 123456' })}
          </div>
        </PartyFormSection>
        <PartyFormSection title="Informazioni aggiuntive">
          <div className="party-form__field">
            <label htmlFor="supplier-referred-by">Riferito da</label>
            <EntityMultiSelect id="supplier-referred-by" label="Riferito da" disabled={isSubmitting}
              options={staff.map(member => ({ ...member, id: member.name }))}
              getLabel={member => member.role ? `${member.name} - ${member.role}` : member.name}
              value={(form.referredBy || '').split('; ').filter(Boolean)}
              onChange={names => updateField('referredBy', names.join('; '))} />
            {staffMessage && <p role="status" className="party-form__help">{staffMessage}</p>}
          </div>
        </PartyFormSection>
        <ClientCustomFieldsSection
          formKey="suppliers.supplier.create"
          values={form.customFields || {}}
          onChange={(customFields) => setForm((previous) => ({ ...previous, customFields }))}
          api={SUPPLIER_CUSTOM_FIELDS_API}
        />

      </form>
      {editing && <SupplierNotesPanel supplier={supplier} />}
    </CreateFormDialog>
  );
}
